/***
 *
 *   MENU
 *   Trigger + anchored Dropdown. This is the part Radix's DropdownMenu gave the
 *   reference and that Phase 2 left open: `align="end"` placement, open/close,
 *   click-outside, Escape and focus return to the trigger.
 *
 *   Placement is CSS-anchored to the trigger (`absolute` + `right-0`/`left-0`)
 *   rather than measured from a rect, so it needs no layout pass and cannot
 *   drift when the page scrolls. `align="end"` therefore cannot overflow the
 *   right edge of the viewport, which was the specific failure the plan called
 *   out. Collision-aware FLIPPING is still not implemented — as with Tooltip,
 *   pick a side that fits.
 *
 *   PROPS
 *   trigger: node rendered inside the trigger button
 *   list: passed straight to Dropdown — see components/dropdown/dropdown.js
 *   align: start (left edge) or end (right edge). Default: start
 *   side: bottom or top. Default: bottom
 *   width: width class for the menu surface. Default: w-56, as the reference
 *   label: aria-label for the trigger
 *   disabled: render the trigger inert
 *   triggerClassName / className: injected classes
 *
 **********/

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from 'components/lib';
import Dropdown from './dropdown';
import Style from './menu.tailwind.js';

export function Menu(props) {
    const { align = 'start', side = 'bottom', width = 'w-56', list } = props;

    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);

    // Focus returns to the trigger on close so a keyboard user is not dumped at
    // the top of the document — Radix does this for the reference.
    const close = useCallback((restoreFocus) => {
        setOpen(false);
        if (restoreFocus) triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e) => {
            if (!wrapperRef.current?.contains(e.target)) close(false);
        };

        // Capture phase: the menu must win over anything the page has bound to
        // Escape (the Pannel, for one) while it is the topmost surface.
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            close(true);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open, close]);

    // Selecting an item closes the menu, as it does in the reference.
    const items = list?.map((item) =>
        item.action
            ? {
                  ...item,
                  action: (...args) => {
                      close(true);
                      item.action(...args);
                  },
              }
            : item
    );

    return (
        <div ref={wrapperRef} className={cn(Style.wrapper, props.className)}>
            <button
                ref={triggerRef}
                type="button"
                disabled={props.disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={props.label}
                onClick={() => (open ? close(false) : setOpen(true))}
                className={cn(Style.trigger, props.triggerClassName)}>
                {props.trigger}
            </button>

            {open && (
                <div className={cn(Style.anchor, Style[align], Style[side], width)}>
                    <Dropdown list={items || []} />
                </div>
            )}
        </div>
    );
}
